import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { apiService } from '../../../services/apiService';
import {
  info,
  todaySplitOf,
  fuelToday,
  dur,
  dt,
  ago,
  clock,
  STATUS_HEX,
  tripOf,
  lastTripOf,
  offTrip,
  addrTip,
  VIO,
  vioToday,
  vioValue,
  ic,
} from './trackingUtils';
import { normalizeViolation, getHistoryDateRange } from './vehicleTracking.adapter';

const VehicleTrackingCard = ({
  vehicle,
  onOpenHistory,
  onShowTripOnMap,
  followVehicle,
  onToggleFollow,
  initialTelemetry = null,
  initialDetail = null,
  onTelemetryLoaded = null,
  onClose = null,
}) => {
  const [showAddrTip, setShowAddrTip] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [telemetryData, setTelemetryData] = useState(initialTelemetry);
  const [detailData, setDetailData] = useState(initialDetail);
  const [tripViolationsList, setTripViolationsList] = useState([]);
  const [tripJourneyData, setTripJourneyData] = useState(null);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [exportingVehiclePdf, setExportingVehiclePdf] = useState(false);
  const { vehicleViolations, tripViolations } = useSelector(
    (state) => state.vehicleTracking || {}
  );

  useEffect(() => {
    if (initialTelemetry) setTelemetryData(initialTelemetry);
    if (initialDetail) setDetailData(initialDetail);
  }, [initialTelemetry, initialDetail]);

  useEffect(() => {
    let isMounted = true;
    if (!vehicle?.id) {
      setTelemetryData(null);
      setDetailData(null);
      return;
    }

    setLoadingDetails(true);
    Promise.allSettled([
      apiService.getFmcrVehicleTrackingCard(vehicle.id),
      apiService.getFmcrVehicleDetail(vehicle.id),
    ])
      .then(([teleRes, detRes]) => {
        if (!isMounted) return;
        let tele = null;
        let det = null;
        if (teleRes.status === 'fulfilled' && teleRes.value) {
          tele = teleRes.value?.data || teleRes.value;
          setTelemetryData(tele);
        }
        if (detRes.status === 'fulfilled' && detRes.value) {
          det = detRes.value?.data || detRes.value;
          setDetailData(det);

          // Fetch real backend trip violations and journey segments
          const tripId = det?.fmcr_trip?.id || det?.fmcr_trip?.trip_id;
          if (tripId) {
            Promise.allSettled([
              apiService.getFmcrTripViolations(tripId),
              apiService.getFmcrTripJourneySegments(tripId),
            ]).then(([vioRes, segRes]) => {
              if (!isMounted) return;
              if (vioRes.status === 'fulfilled' && vioRes.value) {
                const vData = vioRes.value?.data || vioRes.value;
                const rawVios = vData?.violations || (Array.isArray(vData) ? vData : []);
                const mappedVios = rawVios.map(normalizeViolation).filter(Boolean);
                setTripViolationsList(mappedVios);
              }
              if (segRes.status === 'fulfilled' && segRes.value) {
                const sData = segRes.value?.data || segRes.value;
                setTripJourneyData(sData);
              }
            });
          }
        }
        if (onTelemetryLoaded && (tele || det)) {
          onTelemetryLoaded(tele, det);
        }
      })
      .catch((err) => {
        console.warn('Error loading vehicle details:', err);
      })
      .finally(() => {
        if (isMounted) setLoadingDetails(false);
      });

    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicle?.id]);

  if (!vehicle) return null;

  const i = info(vehicle);
  const T = todaySplitOf(vehicle, i);
  const f = fuelToday(vehicle);
  const T_obj = tripOf(vehicle);
  const L_obj = lastTripOf(vehicle);
  const isOffTrip = offTrip(vehicle, i);

  // Live telemetry data overrides
  const curStatus = telemetryData?.current_status;
  const todayTele = telemetryData?.today_telemetry;
  const activeTrip = detailData?.fmcr_trip || (typeof vehicle.trip === 'object' ? vehicle.trip : null);

  const handleExportTripPdf = async () => {
    const tripId = activeTrip?.id || activeTrip?.trip_id;
    if (!tripId) return;
    try {
      setExportingPdf(true);
      const res = await apiService.exportFmcrTripJourneySegmentsPdf(tripId);
      const fileUrl = res?.data?.file_url || res?.file_url;
      if (fileUrl) {
        const link = document.createElement('a');
        const baseUrl = process.env.REACT_APP_API_BASE_URL || '';
        const absoluteUrl = fileUrl.startsWith('http')
          ? fileUrl
          : `${baseUrl.replace(/\/api\/?$/, '')}/${fileUrl}`;
        link.href = absoluteUrl;
        link.target = '_blank';
        link.download = `trip-${activeTrip.trip_id || tripId}-journey.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        alert('PDF URL not returned from server.');
      }
    } catch (err) {
      console.error('Trip PDF Export failed:', err);
      alert(typeof err === 'string' ? err : 'Failed to export trip journey PDF');
    } finally {
      setExportingPdf(false);
    }
  };

  const handleExportVehiclePdf = async () => {
    if (!vehicle?.id) {
      alert('Vehicle ID is required to export journey segments PDF.');
      return;
    }
    try {
      setExportingVehiclePdf(true);
      const { from, to } = getHistoryDateRange('today');
      const res = await apiService.exportFmcrVehicleJourneySegmentsPdf({
        vehicle_id: vehicle.id,
        from,
        to,
      });
      const fileUrl = res?.data?.file_url || res?.file_url;
      if (fileUrl) {
        const link = document.createElement('a');
        const baseUrl = process.env.REACT_APP_API_BASE_URL || '';
        const absoluteUrl = fileUrl.startsWith('http')
          ? fileUrl
          : `${baseUrl.replace(/\/api\/?$/, '')}/${fileUrl}`;
        link.href = absoluteUrl;
        link.target = '_blank';
        link.download = `vehicle-${vehicle.reg || vehicle.id}-journey.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        alert('PDF URL not returned from server.');
      }
    } catch (err) {
      console.error('Vehicle Journey Segments PDF Export failed:', err);
      alert(typeof err === 'string' ? err : 'Failed to export vehicle journey PDF');
    } finally {
      setExportingVehiclePdf(false);
    }
  };

  // Pool live violations from real backend API, local vehicle object, and Redux dynamic sockets
  const reduxVehicleVios = (vehicle?.id && vehicleViolations?.[vehicle.id]) || [];
  const reduxTripVios = (activeTrip?.id && tripViolations?.[activeTrip.id]) || [];
  const combinedVioList = [
    ...(vehicle?.vio || []),
    ...tripViolationsList,
    ...reduxVehicleVios,
    ...reduxTripVios,
  ];

  const seenVios = new Set();
  const violations = vioToday({ vio: combinedVioList }).filter((e) => {
    if (!e) return false;
    // Use type+second as primary key so socket-pushed and API-fetched duplicates are caught.
    const key = `${e.type}-${Math.round((e.t || 0) / 1000)}`;
    if (seenVios.has(key)) return false;
    seenVios.add(key);
    return true;
  });

  const displayLoc = curStatus?.location || i.loc;
  const teleLastSeen = curStatus?.last_seen_at ? new Date(curStatus.last_seen_at).getTime() : 0;
  const vehLastSeen = vehicle?.last ? Number(vehicle.last) : 0;
  // If moving or actively reporting, keep timestamp fresh
  const isActivelyMoving = vehicle.st === 'moving' || (curStatus?.speed != null && Number(curStatus.speed) > 0);
  const lastSeenTs = isActivelyMoving
    ? Math.max(teleLastSeen, vehLastSeen, Date.now() - 30000)
    : (Math.max(teleLastSeen, vehLastSeen) || Date.now());

  const spanTotal = Math.max(1, T.span);
  const drivePct = todayTele?.driving_percent != null
    ? Number(todayTele.driving_percent).toFixed(1)
    : ((T.drive / spanTotal) * 100).toFixed(1);
  const idlePct = todayTele?.idle_percent != null
    ? Number(todayTele.idle_percent).toFixed(1)
    : ((T.idle / spanTotal) * 100).toFixed(1);
  const stopPct = todayTele?.stopped_percent != null
    ? Number(todayTele.stopped_percent).toFixed(1)
    : ((T.stop / spanTotal) * 100).toFixed(1);

  const driveTime = todayTele?.driving_time_formatted || dur(T.drive);
  const idleTime = todayTele?.idle_time_formatted || dur(T.idle);
  const stopTimeSec = todayTele?.stopped_seconds != null ? Number(todayTele.stopped_seconds) : null;
  const stopTime = todayTele?.stopped_time_formatted
    ? (stopTimeSec === 0 ? '—' : todayTele.stopped_time_formatted)
    : dur(T.stop);

  const firstIgnitionAt = todayTele?.first_ignition_at || todayTele?.first_ignition_time;
  const firstIgnition = firstIgnitionAt ? clock(firstIgnitionAt) : (T.start ? clock(T.start) : '—');
  const kmToday = todayTele?.total_km != null ? Math.round(todayTele.total_km) : Math.round(vehicle.km || 0);
  const totalTimeToday = todayTele?.total_time_formatted || dur(T.span);

  const estFuelStr = todayTele?.est_fuel_litres != null
    ? `${todayTele.est_fuel_litres} L`
    : `${f.total.toFixed(1)} L`;
  const avgSpeedStr = todayTele?.avg_speed_kmh != null
    ? `${todayTele.avg_speed_kmh} km/h`
    : `${T.drive > 0 ? Math.round(vehicle.km / (T.drive / 60)) : 0} km/h`;
  const fuelEfficiency = todayTele?.fuel_efficiency_km_l != null
    ? `${todayTele.fuel_efficiency_km_l} km/L`
    : null;

  const tripTele = tripJourneyData?.summary || telemetryData?.trip_telemetry;
  const driverName = detailData?.primary_driver?.username?.trim() || vehicle.drv;
  const driverMobile = detailData?.primary_driver?.mobile?.trim() || vehicle.phone;
  const costCenter = detailData?.fmcr_trip?.cost_center?.name || vehicle.cost_center;
  const odometerVal = detailData?.odm != null
    ? `${Number(detailData.odm).toLocaleString()} km`
    : (vehicle.odo ? `${Number(vehicle.odo).toLocaleString()} km` : null);
  const batteryPct = detailData?.last_log?.battery_percentage;

  const vehTypeTitle = detailData?.vehicle_type?.title || telemetryData?.vehicle_type_title || vehicle.type;
  const vehCategory = vehicle.cat || (detailData?.vehicle_type?.vehicle_class === 'HTV' ? 'HTV' : 'LTV');
  const custName = detailData?.fmcr_trip?.customer?.name || detailData?.customer?.name || vehicle.cust || '—';
  const imeiNo = detailData?.imeino || telemetryData?.imeino || vehicle.dev || '—';

  const COMPASS = (h) =>
    ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'][
      Math.round((((h % 360) + 360) % 360) / 45) % 8
    ];

  const nowLine = () => {
    if (curStatus) {
      const speed = curStatus.speed != null ? Number(curStatus.speed) : (vehicle.spd || 0);
      const ign = curStatus.ignition != null ? (curStatus.ignition ? 'Ignition on' : 'Ignition off') : (i.ign ? `Ignition ${i.ign.toLowerCase()}` : '');
      const durStr = curStatus.state_duration_formatted ? ` · ${curStatus.state_duration_formatted}` : '';
      const idleFuel = curStatus.current_idle_fuel_litres != null && Number(curStatus.current_idle_fuel_litres) > 0
        ? ` · ≈${Number(curStatus.current_idle_fuel_litres).toFixed(1)} L idle fuel`
        : '';

      if (curStatus.status?.toLowerCase() === 'moving' || speed > 0) {
        const hVal = i.h != null && !isNaN(Number(i.h)) ? Number(i.h) : (vehicle.h || 0);
        const heading = hVal !== 0 ? `Heading ${Math.round(hVal)}° ${COMPASS(hVal)} · ` : '';
        return `${heading}${speed} km/h · ${ign}${durStr}`;
      }
      if (curStatus.status?.toLowerCase() === 'idle') {
        return `Idle${durStr} · ${ign}${idleFuel}`;
      }
      return `Stopped${durStr} · ${ign}`;
    }

    if (i.offline) {
      return `Last GPS ${dt(vehicle.last)} · Device not reporting`;
    }
    if (i.status === 'moving') {
      return `Heading ${Math.round(i.h || 0)}° ${COMPASS(i.h || 0)} · Ignition on`;
    }
    if (i.status === 'idle') {
      const idleL = ((vehicle.idle || 10) * 0.03).toFixed(1);
      return `Since ${clock(vehicle.since || Date.now())} · Ignition on · ≈${idleL} L idle fuel`;
    }
    return `Since ${clock(vehicle.since || Date.now())} · Ignition off`;
  };

  return (
    <div className="vd lv-card">
      {onClose && (
        <button
          type="button"
          className="vd-close-btn"
          title="Close"
          aria-label="Close"
          onClick={(e) => {
            e.stopPropagation();
            onClose();
          }}
        >
          <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      )}
      {/* Live status / Location */}
      <div className={`lv-now ${i.offline ? 'off' : ''}`}>
        <div
          className="lv-loc"
          onMouseEnter={() => setShowAddrTip(true)}
          onMouseLeave={() => setShowAddrTip(false)}
          style={{ position: 'relative', cursor: 'help' }}
        >
          <span
            style={{
              display: 'inline-grid',
              placeItems: 'center',
              width: '13px',
              height: '13px',
              flex: 'none',
            }}
            dangerouslySetInnerHTML={{ __html: ic('pin', 'width="13" height="13"') }}
          />
          <span
            className="lv-place"
            style={{
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              wordBreak: 'break-word',
              lineHeight: '1.35',
              maxHeight: '2.7em',
            }}
          >
            {displayLoc}
          </span>
          <span className={`lv-age ${i.offline ? 'off' : ''}`}>
            {i.offline ? `Last known · ${ago(lastSeenTs)}` : ago(lastSeenTs)}
          </span>

          {loadingDetails && (
            <span
              style={{
                marginLeft: 'auto',
                fontSize: '10px',
                color: '#6366f1',
                fontWeight: 600,
                display: 'inline-flex',
                alignItems: 'center',
                gap: '3px',
              }}
            >
              ● Syncing
            </span>
          )}

          {showAddrTip && (
            <div
              className="info-pop"
              style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                zIndex: 100,
                background: '#fff',
                border: '1px solid #ECEAF0',
                borderRadius: '8px',
                padding: '8px 12px',
                boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
                fontSize: '11.5px',
                lineHeight: 1.4,
                width: '260px',
              }}
              dangerouslySetInnerHTML={{
                __html: addrTip(i.p, i.offline ? lastSeenTs : null),
              }}
            />
          )}
        </div>
        <div className="lv-now-2">{nowLine()}</div>
      </div>

      {/* Today summary */}
      <div className="lv-today">
        <div className="hv-lbl sec-h">
          <span>Today · since {firstIgnition} (first ignition)</span>
        </div>
        <div className="hv-big">
          <span>
            <b className="num">{kmToday}</b>
            <small>km</small>
          </span>
          <span>
            <b className="num">{totalTimeToday}</b>
            <small>total</small>
          </span>
        </div>
        <div className="hv-bar" role="img" aria-label="Time split">
          <span
            style={{ width: `${drivePct}%`, background: STATUS_HEX.moving }}
            title={`Driving ${driveTime}`}
          />
          <span
            style={{ width: `${idlePct}%`, background: STATUS_HEX.idle }}
            title={`Idle ${idleTime}`}
          />
          <span
            style={{ width: `${stopPct}%`, background: STATUS_HEX.stopped }}
            title={`Stopped ${stopTime}`}
          />
        </div>
        <div className="hv-leg">
          <span>
            <i style={{ background: STATUS_HEX.moving }} />
            Driving {driveTime}
          </span>
          <span>
            <i style={{ background: STATUS_HEX.idle }} />
            Idle {idleTime}
          </span>
          <span>
            <i style={{ background: STATUS_HEX.stopped }} />
            Stopped {stopTime}
          </span>
        </div>
      </div>

      {/* Fuel, average speed, max speed and fuel efficiency */}
      <div
        className="hv-row3 lv-row"
        style={{
          gridTemplateColumns: `repeat(${
            2 + (todayTele?.max_speed_kmh != null ? 1 : 0) + (fuelEfficiency ? 1 : 0)
          }, 1fr)`,
        }}
      >
        <div>
          <small>Est. fuel today</small>
          <b className="num">{estFuelStr}</b>
        </div>
        <div>
          <small>Avg speed today</small>
          <b className="num">{avgSpeedStr}</b>
        </div>
        {todayTele?.max_speed_kmh != null && (
          <div>
            <small>Max speed today</small>
            <b className="num">{todayTele.max_speed_kmh} km/h</b>
          </div>
        )}
        {fuelEfficiency && (
          <div>
            <small>Fuel efficiency</small>
            <b className="num">{fuelEfficiency}</b>
          </div>
        )}
      </div>

      {/* Key value details */}
      <dl className="lv-kv">
        <dt>Vehicle type</dt>
        <dd>
          <span>{vehCategory}</span> - {vehTypeTitle}
        </dd>
        {driverName && driverName !== 'No Driver' && (
          <>
            <dt>Driver</dt>
            <dd>
              <span>{driverName}</span>
              {driverMobile && (
                <a
                  href={`tel:${driverMobile}`}
                  style={{ marginLeft: '6px', color: '#4014B8', textDecoration: 'none', fontWeight: 600 }}
                  title={`Call ${driverMobile}`}
                >
                  ({driverMobile})
                </a>
              )}
            </dd>
          </>
        )}
        <dt>Customer</dt>
        <dd>{custName}</dd>
        {costCenter && (
          <>
            <dt>Cost Center</dt>
            <dd>{costCenter}</dd>
          </>
        )}
        {odometerVal && (
          <>
            <dt>Odometer</dt>
            <dd className="mono">{odometerVal}</dd>
          </>
        )}
        <dt>Trackingplus IMEI</dt>
        <dd className="mono">{imeiNo}</dd>
        {batteryPct != null && (
          <>
            <dt>Tracker Battery</dt>
            <dd>{batteryPct}%</dd>
          </>
        )}
      </dl>

      {/* Trip Journey Section */}
      {activeTrip ? (
        <div className="tp-sec">
          <div className="hv-lbl sec-h">
            <span>
              Trip <span className="tp-no">{activeTrip.trip_id || `TRIP-${activeTrip.id}`}</span>
            </span>
            <span
              className={`trip-tag ${
                (activeTrip.status || '').toLowerCase().includes('transit')
                  ? 't-it'
                  : (activeTrip.status || '').toLowerCase().includes('complete')
                  ? 't-cp'
                  : 't-aw'
              }`}
            >
              <i />
              {(activeTrip.status || '').toLowerCase().includes('transit')
                ? 'In-Transit'
                : (activeTrip.status || '').toLowerCase().includes('complete')
                ? 'Completed'
                : (activeTrip.status || 'Active')}
            </span>
          </div>

          <div className="tp-jb">
            <div className="tp-jt">
              <span>
                {activeTrip.actual_start_at
                  ? `Started ${clock(activeTrip.actual_start_at)}`
                  : T_obj?.startedTs
                  ? `Started ${clock(T_obj.startedTs)}`
                  : ''}
              </span>
              <span>
                {(activeTrip.status || '').toLowerCase().includes('complete')
                  ? `Arrived ${clock(activeTrip.expected_end_at || Date.now())}`
                  : activeTrip.expected_end_at
                  ? (() => {
                      const etaDate = new Date(activeTrip.expected_end_at);
                      const now = new Date();
                      const dayDiff = Math.round((etaDate.setHours(0,0,0,0) - new Date().setHours(0,0,0,0)) / 86400000);
                      const etaStr = clock(activeTrip.expected_end_at);
                      return dayDiff > 0 ? `ETA ${etaStr} (+${dayDiff}d)` : `ETA ${etaStr}`;
                    })()
                  : ''}
              </span>
            </div>
            <div className="tp-track">
              <span
                className="done"
                style={{
                  width: `${Math.min(
                    100,
                    Math.max(
                      0,
                      Number(
                        activeTrip.trip_meta_info?.covered_percentage ?? (T_obj ? T_obj.frac * 100 : 0)
                      )
                    )
                  ).toFixed(1)}%`,
                }}
              />
              <i className="o" />
              <i className="d" />
            </div>
            <div className="tp-jn">
              <span>{activeTrip.start_trip?.location || activeTrip.dispatch_point || T_obj?.from || '—'}</span>
              <span>{activeTrip.end_trip?.location || activeTrip.decanting_point || T_obj?.to || '—'}</span>
            </div>
            <div className="tp-js">
              <span>on time</span>
              <span>
                {activeTrip.trip_meta_info?.covered_distance != null
                  ? `${Math.round(activeTrip.trip_meta_info.covered_distance)} / ${Math.round(
                      activeTrip.trip_meta_info.total_distance || 0
                    )} km · ${Math.min(100, Math.round(activeTrip.trip_meta_info.covered_percentage || 0))}%`
                  : T_obj
                  ? `${Math.round(T_obj.driven)} / ${Math.round(T_obj.total)} km · ${Math.round(
                      T_obj.frac * 100
                    )}%`
                  : ''}
              </span>
            </div>
          </div>

          {/* Trip Telemetry Metrics (Duration, Driving, Idle, Stopped Split) */}
          {tripTele && (
            <div
              className="tp-telemetry"
              style={{
                marginTop: '10px',
                paddingTop: '8px',
                borderTop: '1px dashed #ECEAF0',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  fontSize: '11px',
                  color: '#475569',
                  fontWeight: 600,
                  marginBottom: '6px',
                }}
              >
                <span>Trip Telemetry · {tripTele.total_trip_km} km</span>
                <span style={{ color: '#64748B', fontWeight: 500 }}>
                  Duration: {tripTele.total_trip_duration_formatted}
                </span>
              </div>
              <div
                className="hv-bar"
                role="img"
                aria-label="Trip Time split"
                style={{ height: '6px', marginBottom: '6px' }}
              >
                <span
                  style={{ width: `${tripTele.driving_percent || 0}%`, background: STATUS_HEX.moving }}
                  title={`Driving ${tripTele.total_driving_formatted}`}
                />
                <span
                  style={{ width: `${tripTele.idle_percent || 0}%`, background: STATUS_HEX.idle }}
                  title={`Idle ${tripTele.total_idle_formatted}`}
                />
                <span
                  style={{ width: `${tripTele.stopped_percent || 0}%`, background: STATUS_HEX.stopped }}
                  title={`Stopped ${tripTele.total_stopped_formatted}`}
                />
              </div>
              <div className="hv-leg" style={{ fontSize: '10.5px' }}>
                <span>
                  <i style={{ background: STATUS_HEX.moving }} />
                  Driving {tripTele.total_driving_formatted}
                </span>
                <span>
                  <i style={{ background: STATUS_HEX.idle }} />
                  Idle {tripTele.total_idle_formatted}
                </span>
                <span>
                  <i style={{ background: STATUS_HEX.stopped }} />
                  Stopped {tripTele.total_stopped_formatted}
                </span>
              </div>
            </div>
          )}

          {/* Trip Stops List */}
          {activeTrip.stops && activeTrip.stops.length > 0 && (
            <div
              className="tp-stops"
              style={{
                marginTop: '10px',
                paddingTop: '8px',
                borderTop: '1px solid #ECEAF0',
              }}
            >
              <div
                style={{
                  fontSize: '11px',
                  fontWeight: 600,
                  color: '#334155',
                  marginBottom: '6px',
                  display: 'flex',
                  justifyContent: 'space-between',
                }}
              >
                <span>Trip Stops ({activeTrip.stops.length})</span>
                <span style={{ fontSize: '10px', color: '#64748B', fontWeight: 400 }}>
                  {activeTrip.stops.filter((s) => s.stop_type === 'load').length} Load ·{' '}
                  {activeTrip.stops.filter((s) => s.stop_type === 'unload').length} Unload
                </span>
              </div>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '4px',
                  maxHeight: '110px',
                  overflowY: 'auto',
                }}
              >
                {activeTrip.stops.map((stop, sIdx) => (
                  <div
                    key={sIdx}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      fontSize: '11px',
                      background: '#F8FAFC',
                      padding: '4px 8px',
                      borderRadius: '4px',
                      border: '1px solid #F1F5F9',
                    }}
                  >
                    <span
                      style={{
                        fontSize: '9px',
                        fontWeight: 700,
                        padding: '1px 5px',
                        borderRadius: '3px',
                        background: stop.stop_type === 'load' ? '#EFF6FF' : '#FEF2F2',
                        color: stop.stop_type === 'load' ? '#2563EB' : '#DC2626',
                        border: `1px solid ${
                          stop.stop_type === 'load' ? '#BFDBFE' : '#FECACA'
                        }`,
                        textTransform: 'uppercase',
                        flexShrink: 0,
                      }}
                    >
                      {stop.stop_type || 'Stop'} #{stop.stop_number || sIdx + 1}
                    </span>
                    <span
                      style={{
                        color: '#475569',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        flex: 1,
                      }}
                      title={stop.location}
                    >
                      {stop.location}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div style={{ display: 'flex', gap: '8px', marginTop: '10px' }}>
            <button
              type="button"
              className="btn sm tp-btn"
              style={{ flex: 1, margin: 0 }}
              onClick={() => onShowTripOnMap && onShowTripOnMap(vehicle.reg)}
            >
              <span dangerouslySetInnerHTML={{ __html: ic('map', 'width="13" height="13"') }} />
              Show trip on map
            </button>
            <button
              type="button"
              className="btn sm outline"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '5px',
                fontSize: '11px',
                fontWeight: 600,
                color: '#4014B8',
                borderColor: '#D4CCF7',
                padding: '4px 10px',
                borderRadius: '6px',
                background: '#F8F6FF',
                cursor: exportingPdf ? 'wait' : 'pointer',
              }}
              disabled={exportingPdf}
              onClick={handleExportTripPdf}
              title="Download Trip Journey Segments PDF report"
            >
              <span dangerouslySetInnerHTML={{ __html: ic('report', 'width="13" height="13"') }} />
              {exportingPdf ? 'Exporting...' : 'Export PDF'}
            </button>
          </div>
        </div>
      ) : T_obj ? (
        <div className="tp-sec">
          <div className="hv-lbl sec-h">
            <span>
              Trip <span className="tp-no">{T_obj.id}</span>
            </span>
            <span
              className={`trip-tag ${
                T_obj.st === 'In-Transit'
                  ? 't-it'
                  : T_obj.st === 'Completed'
                  ? 't-cp'
                  : 't-aw'
              }`}
            >
              <i />
              {T_obj.st}
            </span>
          </div>

          <div className="tp-jb">
            <div className="tp-jt">
              <span>Started {clock(T_obj.startedTs || vehicle.since || Date.now() - 3600000)}</span>
              <span>
                {T_obj.st === 'Completed'
                  ? `Arrived ${clock(T_obj.arrivedTs || Date.now())}`
                  : `ETA ${clock(Date.now() + 3600000)}`}
              </span>
            </div>
            <div className="tp-track">
              <span
                className="done"
                style={{ width: `${(T_obj.frac * 100).toFixed(1)}%` }}
              />
              {T_obj.frac > 0 && T_obj.frac < 1 && (
                <span
                  className="veh"
                  style={{ left: `${(T_obj.frac * 100).toFixed(1)}%` }}
                />
              )}
              <i className="o" />
              <i className="d" />
            </div>
            <div className="tp-jn">
              <span>{T_obj.from}</span>
              <span>{T_obj.to}</span>
            </div>
            <div className="tp-js">
              <span>{T_obj.lateMin >= 10 ? `+${T_obj.lateMin}m late` : 'on time'}</span>
              <span>
                {Math.round(T_obj.driven)} / {Math.round(T_obj.total)} km ·{' '}
                {Math.round(T_obj.frac * 100)}%
              </span>
            </div>
          </div>

          <button
            type="button"
            className="btn sm tp-btn"
            onClick={() => onShowTripOnMap && onShowTripOnMap(vehicle.reg)}
          >
            <span dangerouslySetInnerHTML={{ __html: ic('map', 'width="13" height="13"') }} />
            Show trip on map
          </button>
        </div>
      ) : L_obj ? (
        <div className="tp-sec last">
          <div className="hv-lbl sec-h">
            <span>
              Last trip <span className="tp-no">{L_obj.id}</span>
            </span>
            <span className="trip-tag t-cp">
              <i />Completed
            </span>
          </div>
          <div className="tp-jb last">
            {(L_obj.startTs || L_obj.endTs) && (
              <div className="tp-jt">
                {L_obj.startTs && <span>{dt(L_obj.startTs).slice(0, 6)} {clock(L_obj.startTs)}</span>}
                {L_obj.endTs && <span>{dt(L_obj.endTs).slice(0, 6)} {clock(L_obj.endTs)}</span>}
              </div>
            )}
            <div className="tp-track">
              <span className="done" style={{ width: '100%' }} />
              <i className="o" />
              <i className="d" />
            </div>
            <div className="tp-jn">
              <span>{L_obj.from || '—'}</span>
              <span>{L_obj.to || '—'}</span>
            </div>
            <div className="tp-js">
              {L_obj.total > 0 && <span>{Math.round(L_obj.total)} km</span>}
              {L_obj.endTs && <span>ended {ago(L_obj.endTs)}</span>}
            </div>
          </div>
          <div className="tp-since">
            Since then:{' '}
            {isOffTrip ? (
              <span style={{ color: '#C2255C', fontWeight: 600 }}>Moving now without a trip</span>
            ) : L_obj.endTs && (Date.now() - L_obj.endTs) > 24 * 60 * 60 * 1000 ? (
              <span className="trip-tag t-aw" style={{ background: '#FFF3CD', color: '#B26800', padding: '1px 7px', borderRadius: 4, fontSize: 11 }}>
                Inactive {Math.floor((Date.now() - L_obj.endTs) / (3600 * 1000))}h+
              </span>
            ) : (
              'Awaiting next trip'
            )}
          </div>
        </div>
      ) : (
        <div className="tp-sec">
          <div className="hv-lbl sec-h">
            <span>Trip</span>
            <span className="trip-tag t-aw">
              <i />Awaiting for Trip
            </span>
          </div>
          <div style={{ padding: '6px 0', fontSize: '11.5px', color: '#7A7C96' }}>
            No active trip assigned
          </div>
        </div>
      )}

      {/* Violations List */}
      {violations.length > 0 && (
        <div className="lv-vio">
          <div className="hv-lbl sec-h">
            <span>Today's violations · {violations.length}</span>
          </div>
          <ul>
            {violations.map((e, idx) => (
              <li key={idx}>
                <span className={`vi-ic sev-${e.sev}`}>
                  <span
                    dangerouslySetInnerHTML={{
                      __html: ic(VIO[e.type]?.icon || 'warn', 'width="15" height="15"'),
                    }}
                  />
                </span>
                <span className="vi-t">
                  <b>{VIO[e.type]?.label || e.type}</b> ·{' '}
                  <span className="num">{vioValue(e)}</span>
                  <small>
                    {clock(e.t)} · {displayLoc}
                  </small>
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Actions footer */}
      <div className="vd-act">
        <button
          type="button"
          className="btn sm"
          onClick={() => onOpenHistory && onOpenHistory(vehicle.reg)}
        >
          <span dangerouslySetInnerHTML={{ __html: ic('history') }} />
          View history
        </button>

        <button
          type="button"
          className="btn sm"
          disabled={exportingVehiclePdf}
          onClick={handleExportVehiclePdf}
          title="Download Vehicle Journey Segments PDF report"
          style={{
            marginLeft: '6px',
            color: '#4014B8',
            borderColor: '#D4CCF7',
            background: '#F8F6FF',
            fontWeight: 600,
          }}
        >
          <span dangerouslySetInnerHTML={{ __html: ic('report', 'width="12" height="12"') }} />
          {exportingVehiclePdf ? 'Exporting...' : 'Journey PDF'}
        </button>

        {!i.offline && (
          <label className="check" style={{ marginLeft: 'auto' }}>
            <input
              type="checkbox"
              checked={followVehicle || false}
              onChange={(e) => onToggleFollow && onToggleFollow(e.target.checked)}
            />
            Follow on map
          </label>
        )}
      </div>
    </div>
  );
};

export default VehicleTrackingCard;
